import { type GitpodAPI, type ServiceAccountHeaders } from "@/api";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { defaultRefetchLists, defaultRetry, defaultThrowOnError } from "@/queries/errors";
import { useOrganization } from "@/queries/organization-queries";
import { autoPaginate, parsePaginationToken } from "@/queries/pagination";
import { keyWithPrincipal, serviceAccountPrincipalKey, type PrincipalKey } from "@/queries/principal-key";
import { runnerConfigurationQueryKey } from "@/queries/runner-configuration-queries";
import { useListAvailableRunnerManagers } from "@/queries/runner-manager-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { type PartialMessage, type PlainMessage, toPlainMessage } from "@bufbuild/protobuf";
import { Code, ConnectError } from "@connectrpc/connect";
import {
    type QueryClient,
    skipToken,
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
    type UseQueryResult,
} from "@tanstack/react-query";
import { ResourceOperation, type WatchEventsResponse } from "gitpod-next-api/gitpod/v1/event_pb";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";
import { RunnerKind, RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import {
    type CheckAuthenticationForHostResponse_AuthenticationMethodPersonalAccessToken,
    CreateRunnerPolicyRequest,
    DeleteRunnerPolicyRequest,
    ListRunnerPoliciesRequest,
    ListRunnersRequest,
    ListRunnersRequest_Filter,
    ParseContextURLPreconditionFailureDetails,
    type Runner,
    type RunnerConfiguration,
    RunnerPhase,
    type RunnerPolicy,
    RunnerReleaseChannel,
    RunnerRole,
    RunnerSpec,
    RunnerVariant,
    type SearchMode,
    type SearchRepositoriesResponse_Repository,
    type UpdateRunnerRequest_Spec,
} from "gitpod-next-api/gitpod/v1/runner_pb";
import { PaginationRequest } from "gitpod-next-api/gitpod/v1/pagination_pb";
import { useRef } from "react";

export type PlainRunner = PlainMessage<Runner>;

export function toPlainRunner(runner: Runner): PlainRunner {
    return toPlainMessage(runner);
}

export function toPlainRunnerPolicy(policy: RunnerPolicy): PlainMessage<RunnerPolicy> {
    return toPlainMessage(policy);
}

export const runnerQueryKey = {
    list: (filter: Record<string, string | number | undefined>, principal?: PrincipalKey) =>
        keyWithPrincipal(["runners", "list", filter], principal),
    isUserAuthenticatedWithRunner: (
        runnerId?: string,
        repoURL?: string,
        sessionId?: string,
        serviceAccountId?: string,
    ) => keyWithPrincipal(["runners", "isUserAuthenticated", { runnerId, repoURL, sessionId, serviceAccountId }]),
    parseContext: (runnerId?: string, contextUrl?: string, tokenId?: string, keepOriginalError?: boolean) =>
        keyWithPrincipal([
            "runners",
            "parseContext",
            runnerId ? runnerId : "global",
            { contextUrl, tokenId, keepOriginalError },
        ]),
    get: (runnerId?: string) => keyWithPrincipal(["runners", { runnerId }]),
    listRunnerPolicies: (runnerId: string) => keyWithPrincipal(["runners", "policies", { runnerId }]),
    getLocalConfigurationRunner: () => keyWithPrincipal(["runners", "localConfiguration"]),
    accessToken: (runnerId: string) => keyWithPrincipal(["runners", "accessToken", { runnerId }]),
    logsToken: (runnerId?: string) => keyWithPrincipal(["runners", "logsToken", { runnerId }]),
    ensureOnboardingRunner: () => keyWithPrincipal(["runners", "suitableOnboardingRunner"]),
    searchRepositories: (runnerId?: string, query?: string) =>
        keyWithPrincipal(["runners", "searchRepositories", runnerId ? runnerId : "global", { query }]),
};

export const handleRunnerEvent = async (api: GitpodAPI, client: QueryClient, evt: WatchEventsResponse) => {
    console.debug(`[EventHandler] Runner event:`, {
        operation: evt.operation,
        resourceId: evt.resourceId,
    });

    if (evt.operation === ResourceOperation.UPDATE) {
        const runner = await refetchRunner(api, evt.resourceId);
        if (runner) {
            setRunnerInCache(client, evt.resourceId, toPlainRunner(runner));
            console.debug(`[EventHandler] Runner ${evt.resourceId} updated in cache`);
        } else {
            console.info(`[EventHandler] Runner ${evt.resourceId} not found during update, removing from cache`);
            setRunnerInCache(client, evt.resourceId, undefined);
        }
    } else if (evt.operation === ResourceOperation.CREATE) {
        const prefixOfAllListRunnerKeys = runnerQueryKey.list({}).slice(0, -1);
        await client.invalidateQueries({ queryKey: prefixOfAllListRunnerKeys });
        console.debug(`[EventHandler] Runner ${evt.resourceId} created, lists invalidated`);
    } else if (evt.operation === ResourceOperation.DELETE) {
        setRunnerInCache(client, evt.resourceId, undefined);
        console.debug(`[EventHandler] Runner ${evt.resourceId} deleted from cache`);
    }
};

const refetchRunner = async (api: GitpodAPI, runnerId: string) => {
    try {
        const { runner } = await api.runnerService.getRunner({ runnerId });
        return runner;
    } catch (error) {
        const isNotFound = error instanceof ConnectError && error.code === Code.NotFound;
        // this might happen if the runner was deleted in the meantime
        if (!isNotFound) {
            console.error("Failed to refetch runner", runnerId);
        }
    }
};

type UseParseContextURLProps = {
    enabled?: boolean;
    tokenId?: string;
};

export function useParseContextURL(runnerId?: string, contextUrl?: string, options?: UseParseContextURLProps) {
    const api = useGitpodAPI();
    const query = useQuery({
        queryKey: runnerQueryKey.parseContext(runnerId, contextUrl, options?.tokenId),
        queryFn: async () => {
            if (!contextUrl) {
                throw new Error("Context URL expected");
            }

            try {
                const context = await api.runnerService.parseContextURL({ contextUrl, runnerId });
                if (!context) {
                    throw new Error("Error parsing context URL");
                }
                return context;
            } catch (error) {
                if (
                    error instanceof ConnectError &&
                    error.code === Code.FailedPrecondition &&
                    !["runner is not active", "legal reasons"].some((s) => error.message.includes(s))
                ) {
                    const details = error.findDetails(ParseContextURLPreconditionFailureDetails);
                    throw new ConnectError("Authentication required", Code.Unauthenticated, new Headers(), details);
                }
                throw error;
            }
        },
        throwOnError: false, // do not throw on error, we want to handle the error in the component
        retry: defaultRetry,
        gcTime: 1_000 * 60 * 60 * 24, // 24 hours
        staleTime: 1_000 * 60 * 1, // 1 minute
        enabled: !!contextUrl && (options?.enabled ?? true),
    });

    return { ...query, isLoading: query.isLoading || query.isFetching || query.isPending };
}

export const useParseContext = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ contextUrl, runnerId }: { contextUrl: string; runnerId?: string }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            try {
                const context = await api.runnerService.parseContextURL({ contextUrl, runnerId });
                if (!context) {
                    throw new Error("Error parsing context URL");
                }
                return context;
            } catch (error) {
                if (!(error instanceof Error)) {
                    throw new Error("Unknown error parsing context URL");
                }
                throw error;
            }
        },
    });
};

export type PlainAuthenticationMethodPersonalAccessToken =
    PlainMessage<CheckAuthenticationForHostResponse_AuthenticationMethodPersonalAccessToken>;

export type AuthenticatedWithRunnerResponse =
    | { type: "Authenticated"; scmName: string }
    | {
          type: "AuthenticationRequired";
          url: string;
          patAuth?: PlainAuthenticationMethodPersonalAccessToken;
          scmId: string;
          scmName: string;
      };

type UseIsUserAuthenticatedWithRunnerProps = {
    refetchUntilAuthenticated?: boolean;
    retryOnFailedPrecondition?: boolean;
    sessionId?: string;
    enabled?: boolean;
    headers?: ServiceAccountHeaders;
};

export function useIsUserAuthenticatedWithRunner(
    runnerId?: string,
    repoURL?: string,
    options?: UseIsUserAuthenticatedWithRunnerProps,
) {
    const api = useGitpodAPI();
    const client = useQueryClient();

    const query = useQuery<AuthenticatedWithRunnerResponse>(
        createCheckAuthQueryOptions(api, client, runnerId, repoURL, undefined, options),
    );
    return { ...query, isLoading: query.isLoading || query.isFetching || query.isPending };
}

export function useIsUserAuthenticatedWithRunnerForHost(
    runnerId?: string,
    scmHost?: string,
    options?: UseIsUserAuthenticatedWithRunnerProps,
) {
    const api = useGitpodAPI();
    const client = useQueryClient();
    const query = useQuery<AuthenticatedWithRunnerResponse>(
        createCheckAuthQueryOptions(api, client, runnerId, undefined, scmHost, options),
    );
    return { ...query, isLoading: query.isLoading || query.isFetching || query.isPending };
}

// Helper function to create query options for checking authentication
function createCheckAuthQueryOptions(
    api: GitpodAPI,
    client: QueryClient,
    runnerId?: string,
    repoURL?: string,
    scmHost?: string,
    options?: UseIsUserAuthenticatedWithRunnerProps,
) {
    let host = scmHost;
    if (repoURL) {
        try {
            host = new URL(repoURL).host;
        } catch {
            // ignore invalid URLs
        }
    }

    return {
        queryKey: runnerQueryKey.isUserAuthenticatedWithRunner(
            runnerId,
            repoURL || scmHost,
            options?.sessionId,
            options?.headers?.serviceAccountId,
        ),
        queryFn: async (): Promise<AuthenticatedWithRunnerResponse> => {
            if (!repoURL && !scmHost) {
                throw new Error("Context URL or SCM host expected");
            }

            try {
                const response = await api.runnerService.checkAuthenticationForHost(
                    {
                        host: host,
                        runnerId,
                    },
                    {
                        headers: options?.headers?.headers,
                    },
                );
                if (response.authenticated) {
                    // Invalidate all search repository queries for this runner, regardless of the query string
                    void client.invalidateQueries({
                        queryKey: runnerQueryKey.searchRepositories(runnerId ?? "global").slice(0, -1),
                    });
                    void client.invalidateQueries({
                        queryKey: runnerQueryKey.parseContext(runnerId ?? "global").slice(0, -1),
                    });

                    return { type: "Authenticated", scmName: response.scmName };
                }
                const plainResponse = toPlainMessage(response);
                return {
                    type: "AuthenticationRequired",
                    url: plainResponse.authenticationUrl,
                    patAuth: plainResponse.supportsPat,
                    scmId: plainResponse.scmId,
                    scmName: plainResponse.scmName,
                };
            } catch (error) {
                const isConnectError = error instanceof ConnectError;
                if (!isConnectError) {
                    throw error;
                }

                // throw error;
                // TODO(at) reinstate rethrow after checking if Unimplemented is still an expected case

                if (error.code !== Code.Unimplemented) {
                    throw error;
                }
            }

            return { type: "Authenticated", scmName: "Source Control" };
        },
        refetchInterval: (query: { state: { data?: AuthenticatedWithRunnerResponse } }) => {
            if (options?.refetchUntilAuthenticated) {
                if (query?.state?.data?.type === "AuthenticationRequired") {
                    return 2000;
                }
            }
            return false;
        },
        retry(failureCount: number, error: Error) {
            if (error instanceof ConnectError) {
                switch (error.code) {
                    case Code.DeadlineExceeded:
                        return true;
                    case Code.Unauthenticated:
                    case Code.PermissionDenied:
                    case Code.NotFound:
                    case Code.InvalidArgument:
                    case Code.FailedPrecondition:
                    case Code.Internal:
                        if (options?.retryOnFailedPrecondition) {
                            return true;
                        }
                        return false;
                }
            }
            return failureCount < 5;
        },
        enabled: !!runnerId && !!host && (options?.enabled ?? true),
    };
}

export function useCheckAuthForAllScmIntegrations(
    runnerId?: string,
    scmIntegrationsHosts?: string[],
    options?: {
        refetchConditions?: Record<string, boolean>;
    },
): Record<string, UseQueryResult<AuthenticatedWithRunnerResponse, Error>> {
    const api = useGitpodAPI();
    const client = useQueryClient();

    const queries = useQueries({
        queries:
            scmIntegrationsHosts?.map((scmHost) => {
                const isAwaiting = options?.refetchConditions?.[scmHost] || false;
                return createCheckAuthQueryOptions(api, client, runnerId, undefined, scmHost, {
                    enabled: !!runnerId,
                    refetchUntilAuthenticated: isAwaiting,
                    retryOnFailedPrecondition: true,
                });
            }) ?? [],
        combine: (results) => {
            return results.reduce<Record<string, UseQueryResult<AuthenticatedWithRunnerResponse, Error>>>(
                (acc, result, index) => {
                    acc[scmIntegrationsHosts?.[index] ?? ""] = result;
                    return acc;
                },
                {},
            );
        },
    });

    return queries;
}

type CachedRunnerList = PlainRunner[] | undefined;

function setRunnerInCache(client: QueryClient, runnerId: string, runner?: PlainRunner) {
    client.setQueryData(runnerQueryKey.get(runnerId), runner);
    const prefixOfAllListRunnerKeys = runnerQueryKey.list({}).slice(0, -1);
    client.setQueriesData({ queryKey: prefixOfAllListRunnerKeys }, (currentData?: CachedRunnerList) => {
        if (!currentData) {
            return currentData;
        }
        if (runner) {
            return currentData?.map((r) => (r.runnerId !== runnerId ? r : runner));
        }
        return currentData?.filter((r) => r.runnerId !== runnerId);
    });

    // And need to make sure the runner config is reloaded
    client
        .invalidateQueries({ queryKey: runnerConfigurationQueryKey.listRunnerEnvironmentClasses(runnerId) })
        .catch(console.error);
    client
        .invalidateQueries({ queryKey: runnerConfigurationQueryKey.listRunnerSCMIntegrations(runnerId) })
        .catch(console.error);
    client
        .invalidateQueries({ queryKey: runnerConfigurationQueryKey.getRunnerConfigurationSchema(runnerId) })
        .catch(console.error);
}

export type UseListRunnersParams = {
    kind?: RunnerKind;
    creatorId?: string;
    provider?: RunnerProvider;
    enabled?: boolean;
    headers?: ServiceAccountHeaders;
};

export const useListRunners = ({ kind, creatorId, provider, enabled = true, headers }: UseListRunnersParams) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const query = useQuery({
        queryKey: runnerQueryKey.list(
            { kind, creatorId, provider },
            headers?.serviceAccountId ? serviceAccountPrincipalKey(headers.serviceAccountId) : undefined,
        ),
        queryFn: async (): Promise<PlainRunner[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const request = new ListRunnersRequest({
                filter: new ListRunnersRequest_Filter({
                    // By default, filter out the local configuration runner.
                    kinds: kind ? [kind] : [RunnerKind.LOCAL, RunnerKind.REMOTE],
                    creatorIds: creatorId ? [creatorId] : undefined,
                    providers: provider ? [provider] : undefined,
                }),
            });
            const runners = await autoPaginate(
                (params) => api.runnerService.listRunners(params, headers ? { headers: headers.headers } : undefined),
                request,
                (response) => response.runners,
            );

            return runners.map(toPlainRunner);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: enabled && !!user,
        gcTime: 1_000 * 60 * 60 * 24, // 24 hours
        staleTime: 1_000 * 60 * 1, // 1 minute

        // refetch on reconnect, window focus, and every 5 minutes
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchIntervalInBackground: true,
        refetchInterval: defaultRefetchLists,
    });
    return { ...query, runners: query.data || [] };
};

/**
 * Hook to list dev runners (RUNNER_PROVIDER_DEV_AGENT) for the current user.
 * Used for the dev runner selector in the Ona home screen.
 */
export const useListDevRunners = (enabled = true) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    const query = useQuery({
        queryKey: runnerQueryKey.list({ provider: RunnerProvider.DEV_AGENT }),
        queryFn: async (): Promise<PlainRunner[]> => {
            if (!user) {
                throw new Error("User not authenticated");
            }
            const request = new ListRunnersRequest({
                filter: new ListRunnersRequest_Filter({
                    providers: [RunnerProvider.DEV_AGENT],
                }),
            });
            const runners = await autoPaginate(
                (params) => api.runnerService.listRunners(params),
                request,
                (response) => response.runners,
            );

            return runners.map(toPlainRunner);
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: enabled && !!user,
        staleTime: 1_000 * 30, // 30 seconds - dev runners change more frequently
        refetchOnReconnect: "always",
        refetchOnWindowFocus: "always",
        refetchInterval: 1_000 * 30, // 30 seconds
    });
    return { ...query, devRunners: query.data || [] };
};

export const useRunner = (runnerId?: string) => {
    const api = useGitpodAPI();

    return useQuery<PlainRunner, Error>({
        queryKey: runnerQueryKey.get(runnerId),
        queryFn: async () => {
            if (!runnerId) {
                throw new Error("No runnerId provided");
            }

            const { runner } = await api.runnerService.getRunner({
                runnerId,
            });

            if (!runner) {
                throw new Error("Error fetching runner");
            }

            return toPlainRunner(runner);
        },
        enabled: !!runnerId,
    });
};

export const useLocalConfigurationRunner = () => {
    const api = useGitpodAPI();

    return useQuery<PlainRunner | undefined, Error>({
        queryKey: runnerQueryKey.getLocalConfigurationRunner(),
        queryFn: async () => {
            const runners = await autoPaginate(
                (params) => api.runnerService.listRunners(params),
                new ListRunnersRequest({
                    filter: new ListRunnersRequest_Filter({
                        kinds: [RunnerKind.LOCAL_CONFIGURATION],
                    }),
                }),
                (response) => response.runners,
            );

            // runners can be empty (e.g. when not an org admin, or does not exist).
            if (runners.length === 0) {
                return undefined;
            }

            return toPlainRunner(runners[0]);
        },
    });
};

export const useSetLocalRunnersEnabled = (localConfigRunner: PlainRunner) => {
    const client = useQueryClient();
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ enabled }: { enabled: boolean }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { runnerId } = localConfigRunner;
            await api.runnerService.updateRunner({
                runnerId,
                spec: {
                    desiredPhase: enabled ? RunnerPhase.ACTIVE : RunnerPhase.INACTIVE,
                },
            });

            if (!localConfigRunner.spec) {
                localConfigRunner.spec = toPlainMessage(new RunnerSpec());
            }
            localConfigRunner.spec.desiredPhase = enabled ? RunnerPhase.ACTIVE : RunnerPhase.INACTIVE;
            setRunnerInCache(client, runnerId, localConfigRunner);
        },
    });
};

export const useCreateRemoteRunner = () => {
    const client = useQueryClient();
    const api = useGitpodAPI();

    return useMutation({
        mutationFn: async ({
            name,
            region,
            provider,
            variant,
        }: {
            name: string;
            region?: string;
            provider: RunnerProvider;
            variant?: RunnerVariant;
        }) => {
            const configuration: PartialMessage<RunnerConfiguration> = {
                region,
                autoUpdate: true,
                releaseChannel: RunnerReleaseChannel.STABLE,
            };
            if (provider === RunnerProvider.AWS_EC2) {
                configuration.devcontainerImageCacheEnabled = true;
            }

            const { runner } = await api.runnerService.createRunner({
                name,
                spec: {
                    configuration,
                    variant,
                },
                provider,
            });

            if (!runner) {
                throw new Error("Error creating runner");
            }

            return {
                runner: toPlainRunner(runner),
            };
        },
        onSuccess: async () => {
            const prefixOfAllListRunnerKeys = runnerQueryKey.list({}).slice(0, -1);
            await client.invalidateQueries({ queryKey: prefixOfAllListRunnerKeys });
        },
    });
};

export const useCreateRunnerAccessToken = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (runnerId: string) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { accessToken } = await api.runnerService.createRunnerToken({
                runnerId: runnerId,
            });

            return {
                accessToken,
            };
        },
    });
};

export const useRunnerAccessToken = (runnerId: string, options?: { enabled?: boolean }) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: runnerQueryKey.accessToken(runnerId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { accessToken } = await api.runnerService.createRunnerToken({
                runnerId: runnerId,
            });

            return {
                accessToken,
            };
        },
        enabled: options?.enabled ?? true,
        staleTime: 1000 * 60 * 60, // 1 hour - access tokens are typically long-lived
        retry: defaultRetry,
        throwOnError: defaultThrowOnError,
    });
};

export const useCreateRunnerLogsToken = (runnerId?: string) => {
    const api = useGitpodAPI();
    const tokenValidDuration = 1000 * 60 * 45;
    return useQuery<string, Error>({
        queryKey: runnerQueryKey.logsToken(runnerId),
        queryFn: async (): Promise<string> => {
            if (!runnerId) {
                throw new Error("No runner ID provided");
            }

            const { accessToken } = await api.runnerService.createRunnerLogsToken({ runnerId });
            if (!accessToken) {
                throw new Error("No token provided");
            }

            return accessToken;
        },
        staleTime: tokenValidDuration,
        gcTime: tokenValidDuration,
        enabled: !!runnerId,
    });
};

export const useCreateRunnerExchangeToken = () => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async (runnerId: string) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const { exchangeToken } = await api.runnerService.createRunnerToken({
                runnerId: runnerId,
            });

            return {
                exchangeToken,
            };
        },
    });
};

export const useCreateManagedRunner = () => {
    const api = useGitpodAPI();
    const queryClient = useQueryClient();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({
            runnerManagerId,
            name,
            region,
        }: {
            runnerManagerId: string;
            name: string;
            region: string;
        }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const configuration: PartialMessage<RunnerConfiguration> = {
                region,
                autoUpdate: false,
                releaseChannel: RunnerReleaseChannel.STABLE,
                devcontainerImageCacheEnabled: true,
            };

            const response = await api.runnerService.createRunner({
                name,
                provider: RunnerProvider.MANAGED,
                runnerManagerId,
                spec: {
                    configuration,
                    variant: RunnerVariant.STANDARD,
                },
            });

            if (!response.runner) {
                throw new Error("Failed to create managed runner");
            }

            return toPlainRunner(response.runner);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: runnerQueryKey.list({}) });
        },
        onError: (error: unknown) => {
            console.error("Failed to create managed runner:", error);
        },
    });
};

export const useHasManagedRunner = () => {
    const { data, isLoading, isPending, isError, error, refetch, status } = useListRunners({
        provider: RunnerProvider.MANAGED,
        kind: RunnerKind.REMOTE,
    });

    return {
        data: !data ? false : data.length > 0,
        isLoading,
        isPending,
        isError,
        error,
        refetch,
        status,
    };
};

export const useUpdateRunner = () => {
    const client = useQueryClient();
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({
            runnerId,
            name,
            spec,
        }: {
            runnerId: string;
            name?: string;
            spec?: PartialMessage<UpdateRunnerRequest_Spec>;
        }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            await api.runnerService.updateRunner({
                runnerId,
                name,
                spec,
            });

            const { runner } = await api.runnerService.getRunner({
                runnerId,
            });

            if (!runner) {
                throw new Error("Error fetching runner after update");
            }
            setRunnerInCache(client, runnerId, toPlainRunner(runner));
        },
    });
};

export const useDeleteRunner = () => {
    const client = useQueryClient();
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useMutation({
        mutationFn: async ({ runnerId, force = false }: { runnerId: string; force: boolean }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            return await api.runnerService.deleteRunner({
                runnerId,
                force,
            });
        },
        onSuccess: async () => {
            const prefixOfAllListRunnerKeys = runnerQueryKey.list({}).slice(0, -1);
            await client.invalidateQueries({ queryKey: prefixOfAllListRunnerKeys });
        },
    });
};

export const useListRunnerPolicies = (runnerId: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();

    return useQuery({
        queryKey: runnerQueryKey.listRunnerPolicies(runnerId),
        queryFn: async () => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            const policies = await autoPaginate(
                (params) => api.runnerService.listRunnerPolicies(params),
                new ListRunnerPoliciesRequest({
                    runnerId: runnerId,
                }),
                (response) => response.policies,
            );

            return {
                policies: policies.map(toPlainRunnerPolicy),
            };
        },
        throwOnError: defaultThrowOnError,
        retry: defaultRetry,
        enabled: !!user,
        staleTime: 500,
    });
};

export const useUpdateSharingSetting = (runnerId: string) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const client = useQueryClient();

    return useMutation({
        mutationFn: async ({ setting, groupId }: { setting: "only-me" | "anyone-in-org"; groupId: string }) => {
            if (!user) {
                throw new Error("User not authenticated");
            }

            if (setting === "only-me") {
                await api.runnerService.deleteRunnerPolicy(
                    new DeleteRunnerPolicyRequest({
                        runnerId,
                        groupId,
                    }),
                );
            } else if (setting === "anyone-in-org") {
                await api.runnerService.createRunnerPolicy(
                    new CreateRunnerPolicyRequest({
                        runnerId,
                        groupId,
                        role: RunnerRole.USER,
                    }),
                );
            }
        },
        onSettled: async () => {
            await client.invalidateQueries({ queryKey: runnerQueryKey.listRunnerPolicies(runnerId) });
        },
    });
};

const US_RUNNER_REGION = "us-east-1";
const EU_RUNNER_REGION = "eu-central-1";

type Region = typeof US_RUNNER_REGION | typeof EU_RUNNER_REGION;

function closestManagedRunnerRegion(): Region {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (tz.startsWith("America")) return US_RUNNER_REGION;
    if (tz.startsWith("Europe") || tz.startsWith("Africa")) return EU_RUNNER_REGION;
    if (tz.startsWith("Asia") || tz.startsWith("Pacific") || tz.startsWith("Australia")) return US_RUNNER_REGION; // closer westward
    if (tz.startsWith("Indian") || tz.startsWith("Antarctica")) return EU_RUNNER_REGION; // closer eastward

    // fallback for unknown or weird zones
    return EU_RUNNER_REGION;
}

/**
 * Queries for remote runners in the org and returns one. If there's zero, creates a new managed one and returns it.
 */
export const useEnsureOnboardingRunner = ({ enabled = true }: { enabled?: boolean }) => {
    const { data: remoteRunners } = useListRunners({
        kind: RunnerKind.REMOTE,
    });
    const createManagedRunner = useCreateManagedRunner();
    const { availableRunnerManagers, isPending: isRunnerManagersPending } = useListAvailableRunnerManagers();
    const userPreferredRegion = closestManagedRunnerRegion();
    const { data: org } = useOrganization();
    const api = useGitpodAPI();
    const runnerRefetchCount = useRef(0);

    const ensureOnboardingRunnerQuery = useQuery({
        queryKey: runnerQueryKey.ensureOnboardingRunner(),
        queryFn: async () => {
            if (!remoteRunners) {
                return;
            }

            const suitableManagedRunner = remoteRunners.find(
                (runner) =>
                    runner.provider === RunnerProvider.MANAGED &&
                    runner.spec?.configuration?.region === userPreferredRegion,
            );

            if (!suitableManagedRunner) {
                if (availableRunnerManagers.length === 0 || org?.tier === OrganizationTier.ENTERPRISE) {
                    throw new Error("No available runner managers to create a managed runner");
                } else {
                    const firstAvailableManager =
                        availableRunnerManagers.find((manager) => manager.region === userPreferredRegion) ??
                        availableRunnerManagers[0];
                    try {
                        return await createManagedRunner.mutateAsync({
                            name: firstAvailableManager.name,
                            region: firstAvailableManager.region,
                            runnerManagerId: firstAvailableManager.runnerManagerId,
                        });
                    } catch (error) {
                        // TODO: handle runner not active case
                        if (error instanceof ConnectError && error.code === Code.AlreadyExists) {
                            // we are not able to find a managed runner in user's preferred region
                            // so the mutate call attempted to create a runner in the same region as the first available manager
                            // which throws an already exists error, which we then need to catch and find the runner in the same region as the first available manager
                            return remoteRunners.find(
                                (runner) =>
                                    runner?.status?.phase === RunnerPhase.ACTIVE &&
                                    runner.spec?.configuration?.region === firstAvailableManager.region,
                            );
                        }
                        throw error;
                    }
                }
            }

            // TODO: potential unnecessary find call
            const suitableRemoteRunner = remoteRunners.find((runner) => runner?.status?.phase === RunnerPhase.ACTIVE);
            if (!suitableManagedRunner && !suitableRemoteRunner) {
                throw new Error("No suitable runner found. Please create one manually to proceed.");
            }

            return suitableManagedRunner ?? suitableRemoteRunner;
        },
        enabled: !!remoteRunners && !!org && enabled && !isRunnerManagersPending,
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: false,
    });

    const {
        data: runner,
        isLoading: isLoadingRunner,
        error: runnerQueryError,
    } = useQuery({
        queryKey: runnerQueryKey.get(ensureOnboardingRunnerQuery.data?.runnerId),
        queryFn: async () => {
            if (runnerRefetchCount.current > 10) {
                throw new Error("timed out when waiting for runner to be ready");
            }

            const runnerId = ensureOnboardingRunnerQuery.data?.runnerId;
            if (!runnerId) {
                throw new Error("No runnerId provided");
            }

            const { runner } = await api.runnerService.getRunner({
                runnerId,
            });

            if (!runner) {
                throw new Error("GetRunner rpc call returned no runner");
            }

            runnerRefetchCount.current += 1;

            return toPlainRunner(runner);
        },
        refetchInterval: (query) => (query.state.data?.status?.phase !== RunnerPhase.ACTIVE ? 500 : false),
        enabled: !!ensureOnboardingRunnerQuery.data?.runnerId && enabled,
    });

    return {
        data: runner?.status?.phase === RunnerPhase.ACTIVE ? runner : null,
        isLoading: ensureOnboardingRunnerQuery.isLoading || isLoadingRunner,
        error: ensureOnboardingRunnerQuery.error ?? runnerQueryError ?? null,
    };
};

export const useSearchRepositoriesQuery = ({
    runnerId,
    query,
    limit,
    scmHost,
    searchMode,
    enabled = true,
}: {
    runnerId: string;
    query: string;
    limit: number;
    scmHost: string;
    searchMode: SearchMode;
    enabled?: boolean;
}) => {
    const api = useGitpodAPI();
    return useQuery({
        queryKey: runnerQueryKey.searchRepositories(runnerId, query),
        queryFn: scmHost
            ? async () => {
                  const { repositories } = await api.runnerService.searchRepositories(
                      {
                          runnerId,
                          searchString: query,
                          limit,
                          scmHost,
                          searchMode,
                      },
                      { timeoutMs: 30 * 1000 },
                  );
                  return repositories.map(toPlainMessage);
              }
            : skipToken,
        enabled: !!runnerId && !!scmHost && enabled,
    });
};

export type PlainSearchRepository = PlainMessage<SearchRepositoriesResponse_Repository>;

/**
 * Page data returned by useSearchRepositoriesPaginated.
 */
export type SearchRepositoriesPage = {
    repositories: PlainSearchRepository[];
    nextToken: string | null;
    /** Total count of matching repositories. -1 if unknown (e.g., GitLab broad searches). */
    totalCount: number;
    /** Total pages available. -1 if unknown. */
    totalPages: number;
};

/**
 * Paginated repository search hook using useInfiniteQuery.
 * Fetches repositories in pages of 25 by default.
 * Callers can use fetchNextPage/hasNextPage to load more results.
 *
 * The first page contains totalCount/totalPages from the pagination token.
 * Use these to show "X of N" in the UI. -1 means unknown.
 */
export const useSearchRepositoriesPaginated = ({
    runnerId,
    query,
    scmHost,
    searchMode,
    enabled = true,
    pageSize = 25,
}: {
    runnerId: string;
    query: string;
    scmHost: string;
    searchMode: SearchMode;
    enabled?: boolean;
    pageSize?: number;
}) => {
    const api = useGitpodAPI();
    return useInfiniteQuery({
        queryKey: [...runnerQueryKey.searchRepositories(runnerId, query), { scmHost, searchMode, pageSize }],
        queryFn: async ({ pageParam }): Promise<SearchRepositoriesPage> => {
            const response = await api.runnerService.searchRepositories(
                {
                    runnerId,
                    searchString: query,
                    scmHost,
                    searchMode,
                    pagination: new PaginationRequest({ pageSize, token: pageParam }),
                },
                { timeoutMs: 30 * 1000 },
            );

            // Parse pagination token to extract total info
            const tokenInfo = parsePaginationToken(response.pagination?.nextToken);

            return {
                repositories: response.repositories.map(toPlainMessage),
                nextToken: response.pagination?.nextToken || null,
                totalCount: tokenInfo?.totalCount ?? -1,
                totalPages: tokenInfo?.totalPages ?? -1,
            };
        },
        initialPageParam: "",
        getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
        enabled: !!runnerId && !!scmHost && enabled,
    });
};

/**
 * Result from a single runner's repository search.
 */
export type RunnerSearchResult = {
    runnerId: string;
    repositories: PlainSearchRepository[];
    isLoading: boolean;
    isError: boolean;
    error: Error | null;
};

/**
 * Combined result from searching repositories across multiple runners.
 */
export type SearchRepositoriesAcrossRunnersResult = {
    /** Deduplicated repositories from all runners, keyed by URL */
    repositories: PlainSearchRepository[];
    /** Per-runner results for debugging or showing runner-specific info */
    byRunner: RunnerSearchResult[];
    /** True if any runner query is still loading */
    isLoading: boolean;
    /** True if all runner queries have completed (success or error) */
    isSettled: boolean;
    /** True if any runner query has an error */
    hasErrors: boolean;
};

/**
 * Search repositories across multiple runners in parallel.
 * Results are deduplicated by repository URL, keeping the first occurrence.
 *
 * Unlike useSearchRepositoriesPaginated, this hook fetches only the first page
 * from each runner. Use this for initial search results across all runners.
 */
export const useSearchRepositoriesAcrossRunners = ({
    runnerIds,
    query,
    scmHost,
    searchMode,
    enabled = true,
    pageSize = 25,
}: {
    runnerIds: string[];
    query: string;
    scmHost: string;
    searchMode: SearchMode;
    enabled?: boolean;
    pageSize?: number;
}): SearchRepositoriesAcrossRunnersResult => {
    const api = useGitpodAPI();

    const queries = useQueries({
        queries: runnerIds.map((runnerId) => ({
            queryKey: [...runnerQueryKey.searchRepositories(runnerId, query), { scmHost, searchMode, pageSize }],
            queryFn: async (): Promise<{ runnerId: string; repositories: PlainSearchRepository[] }> => {
                const response = await api.runnerService.searchRepositories(
                    {
                        runnerId,
                        searchString: query,
                        scmHost,
                        searchMode,
                        pagination: new PaginationRequest({ pageSize }),
                    },
                    { timeoutMs: 30 * 1000 },
                );
                return {
                    runnerId,
                    repositories: response.repositories.map(toPlainMessage),
                };
            },
            enabled: enabled && !!scmHost && runnerIds.length > 0,
        })),
    });

    // Build per-runner results
    const byRunner: RunnerSearchResult[] = queries.map((q, idx) => ({
        runnerId: runnerIds[idx],
        repositories: q.data?.repositories ?? [],
        isLoading: q.isLoading,
        isError: q.isError,
        error: q.error,
    }));

    // Deduplicate repositories by URL
    const seen = new Set<string>();
    const repositories: PlainSearchRepository[] = [];
    for (const result of byRunner) {
        for (const repo of result.repositories) {
            if (!seen.has(repo.url)) {
                seen.add(repo.url);
                repositories.push(repo);
            }
        }
    }

    return {
        repositories,
        byRunner,
        isLoading: queries.some((q) => q.isLoading),
        isSettled: queries.every((q) => q.isSuccess || q.isError),
        hasErrors: queries.some((q) => q.isError),
    };
};

/**
 * SCM Organization returned by useListSCMOrganizationsAcrossRunners.
 */
export type PlainSCMOrganization = {
    name: string;
    url: string;
    isAdmin: boolean;
};

/**
 * List SCM organizations across multiple runners in parallel.
 * Results are deduplicated by organization name, keeping the first occurrence.
 */
export const useListSCMOrganizationsAcrossRunners = ({
    runnerIds,
    scmHost,
    enabled = true,
}: {
    runnerIds: string[];
    scmHost: string;
    enabled?: boolean;
}): {
    organizations: PlainSCMOrganization[];
    isLoading: boolean;
    isFetching: boolean;
} => {
    const api = useGitpodAPI();

    const queries = useQueries({
        queries: runnerIds.map((runnerId) => ({
            queryKey: ["scmOrganizations", runnerId, scmHost],
            queryFn: async (): Promise<PlainSCMOrganization[]> => {
                const response = await api.runnerService.listSCMOrganizations({
                    runnerId,
                    scmHost,
                });
                return response.organizations.map((org) => ({
                    name: org.name,
                    url: org.url,
                    isAdmin: org.isAdmin,
                }));
            },
            enabled: enabled && !!scmHost && runnerIds.length > 0,
        })),
    });

    // Deduplicate organizations by name
    const seen = new Set<string>();
    const organizations: PlainSCMOrganization[] = [];
    for (const query of queries) {
        for (const org of query.data ?? []) {
            if (!seen.has(org.name)) {
                seen.add(org.name);
                organizations.push(org);
            }
        }
    }

    return {
        organizations,
        isLoading: queries.some((q) => q.isLoading),
        isFetching: queries.some((q) => q.isFetching),
    };
};
